import React, { useState, useEffect } from "react";
import "./InvitFinancial.less";
import { formatNumber } from "../../../../utils/financialHelper";
import {
  DistributionPerUnit,
  FinancialRecord,
  Invit,
  QuarterDataFinancialMetric,
} from "../../../../types/invit.types";

interface InvitFinancialProps {
  invit: Invit | null;
}

interface MetricRow {
  metric: string;
  values: (string | number)[];
}

const InvitFinancial: React.FC<InvitFinancialProps> = ({ invit }) => {
  const displayName = "cmp-invit-financial";
  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>("");
  const [years, setYears] = useState<string[]>([]);
  const [financialData, setFinancialData] = useState<MetricRow[]>([]);
  const [quartersToShow, setQuartersToShow] = useState<
    QuarterDataFinancialMetric[]
  >([]);

  useEffect(() => {
    if (!invit || !invit.financialMetrics) return;

    // Extract all year labels
    const yearsList = invit.financialMetrics.map((f) => f.year);

    // Sort descending by first numeric part (e.g. "FY 2024-25" → 2024)
    const sortedYears = [...yearsList].sort((a, b) => {
      const yearA = parseInt(a.match(/\d{4}/)?.[0] || "0", 10);
      const yearB = parseInt(b.match(/\d{4}/)?.[0] || "0", 10);
      return yearB - yearA;
    });

    setYears(sortedYears);
    setYear(sortedYears[0] || ""); // latest year as default
  }, [invit]);

  useEffect(() => {
    if (!year || !invit || !invit.financialMetrics) return;

    const yearData = invit.financialMetrics.find((f) => f.year === year);
    if (!yearData) return;

    let quarters: QuarterDataFinancialMetric[] = [];

    if (view === "Quarterly") {
      quarters = yearData.quarters;
    } else {
      const reversedQuarters = [...yearData.quarters].reverse();
      const lastQuarter = reversedQuarters.find((q) =>
        Object.values(q).some(
          (val) =>
            val &&
            typeof val === "object" &&
            "value" in val &&
            val.value !== null &&
            val.value !== undefined
        )
      );
      if (lastQuarter) quarters = [lastQuarter];
    }

    setQuartersToShow(quarters);

    const metricsMap: Record<string, (string | number)[]> = {};

    quarters.forEach((q: QuarterDataFinancialMetric) => {
      Object.keys(q).forEach((key) => {
        if (key === "quarter") return;
        const record = (q as any)[key] as
          | FinancialRecord
          | DistributionPerUnit
          | undefined;
        if (!record) return;

        if (
          key === "distributionPerUnitQuarterly" ||
          key === "distributionPerUnitYTD"
        ) {
          const mainValue =
            typeof record.value === "boolean"
              ? record.value
                ? "Yes"
                : "No"
              : formatNumber(record.value);

          const mainKey = `${record.key} (${
            key === "distributionPerUnitQuarterly" ? "Quarterly" : "YTD"
          })`;
          if (!metricsMap[mainKey]) metricsMap[mainKey] = [];
          metricsMap[mainKey].push(mainValue);

          ["dividend", "capitalRepayment", "interest", "otherIncome"].forEach(
            (subKey) => {
              const subRecord = (record as any)[subKey] as
                | FinancialRecord
                | undefined;
              if (!subRecord) return;

              const subVal =
                typeof subRecord.value === "boolean"
                  ? subRecord.value
                    ? "Yes"
                    : "No"
                  : formatNumber(subRecord.value);

              const subMetricKey = `${subRecord.key} (${
                key === "distributionPerUnitQuarterly" ? "Quarterly" : "YTD"
              })`;

              if (!metricsMap[subMetricKey]) metricsMap[subMetricKey] = [];
              metricsMap[subMetricKey].push(subVal);
            }
          );
          return;
        }

        const metricKey = record?.key ?? key;
        const metricValue = record?.value;
        const formattedValue =
          typeof metricValue === "boolean"
            ? metricValue
              ? "Yes"
              : "No"
            : formatNumber(metricValue);

        if (!metricsMap[metricKey]) metricsMap[metricKey] = [];
        metricsMap[metricKey].push(formattedValue);
      });
    });

    Object.keys(metricsMap).forEach((key) => {
      while (metricsMap[key].length < quarters.length) {
        metricsMap[key].push("--");
      }
    });

    const tableData: MetricRow[] = Object.keys(metricsMap).map((key) => ({
      metric: key,
      values: metricsMap[key],
    }));

    setFinancialData(tableData);
  }, [year, invit, view]);

  return (
    <section className={displayName}>
      <div className={`${displayName}__card`}>
        <div className={`${displayName}__header`}>
          <h2 className={`${displayName}__title`}>Financial Metrics</h2>
          <div className={`${displayName}__subheader`}>
            <div className={`${displayName}__tabs`}>
              <button
                className={`${displayName}__tab ${
                  view === "Yearly" ? `${displayName}__tab--active` : ""
                }`}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={`${displayName}__tab ${
                  view === "Quarterly" ? `${displayName}__tab--active` : ""
                }`}
                onClick={() => setView("Quarterly")}
              >
                Quarterly
              </button>
            </div>
            <div className={`${displayName}__dropdown`}>
              <select
                className={`${displayName}__select`}
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                {[...years]
                  .sort((a, b) => {
                    // extract the first year number, e.g. "FY 2024-25" → 2024
                    const yearA = parseInt(a.match(/\d{4}/)?.[0] || "0", 10);
                    const yearB = parseInt(b.match(/\d{4}/)?.[0] || "0", 10);
                    return yearB - yearA; // descending order
                  })
                  .map((y, idx) => (
                    <option key={idx} value={y}>
                      {y}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        <div className={`${displayName}__table`}>
          <table className={`${displayName}__table-wrapper`}>
            <thead>
              <tr>
                <th className={`${displayName}__table-head-cell`}>Metrics</th>
                {view === "Quarterly" ? (
                  <>
                    <th className={`${displayName}__table-head-cell`}>Q1</th>
                    <th className={`${displayName}__table-head-cell`}>Q2</th>
                    <th className={`${displayName}__table-head-cell`}>Q3</th>
                    <th className={`${displayName}__table-head-cell`}>Q4</th>
                  </>
                ) : (
                  <th className={`${displayName}__table-head-cell`}>
                    {quartersToShow[0]?.quarter}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {financialData.map((row, idx) => (
                <tr key={idx} className={`${displayName}__table-row`}>
                  <td
                    className={`${displayName}__table-cell ${
                      row.metric.startsWith("-")
                        ? "child-metric"
                        : "parent-metric"
                    }`}
                  >
                    {row.metric}
                  </td>

                  {row.values.map((val, i) => (
                    <td key={i} className={`${displayName}__table-cell`}>
                      {val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default InvitFinancial;
